import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { getGames } from "../../../../api/game";
import { getTexts, createText, updateText } from "../../../../api/text";
import { alertError, alertSuccess } from "../../../../utils/logger";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import Table from "../../../components/tables/table";
import Editor from "../../../components/editor/Editor";

// Metadatos por key: order, scenario y defaultValue para create/update
const KEY_META = {
	"FT.title": { order: -2, scenario: 0, defaultValue: "Accede a la ficha técnica de Lupkynis" },
	"FT.content": {
		order: -1,
		scenario: 0,
		defaultValue: `<p><strong>CONDICIONES DE PRESCRIPCIÓN Y DISPENSACIÓN</strong></p>
<p>Medicamento sujeto a prescripción médica. Dispensación hospitalaria sin cupón precinto.</p>
<p><strong>CONDICIONES DE PRESTACIÓN DEL SISTEMA NACIONAL DE SALUD</strong></p>
<p>Reembolsable por el Sistema Nacional de Salud.</p>
<p><strong>PRESENTACIONES Y PRECIO</strong></p>
<p>Lupkynis 7,9 mg cápsulas blandas, 180 cápsulas. Precio Notificado autorizado: 780€.</p>`,
	},
	"context.intro": { order: 0, scenario: 0, defaultValue: "<p>Esta es Martina, 37 años. Le han diagnosticado <strong>lupus eritematoso sistémico</strong> (LES) <strong>y una proteinuria elevada</strong>.</p>" },
	"context.startGameModal": {
		order: 0,
		scenario: 0,
		defaultValue: `<div class="d-flex flex-column align-items-center justify-content-center">
			<div class="txt-turquoise txt20">
				<strong>Ayuda a Martina resolviendo el caso.</strong>
			</div>
			<div class="d-flex align-items-start mt-4">
				<ul>
					<li>
						Es fundamental que
						<strong>trabajéis en equipo</strong>: recopilad
						todos los datos y responded correctamente a las
						preguntas.
					</li>
					<li>
						Las imágenes con la descripción correcta se quedarán
						fijas.
					</li>
					<li>
						Ganará el equipo que consiga una puntuación más alta.
					</li>
				</ul>
			</div>
		</div>`,
	},
	"end.footerRef": { order: 0, scenario: 9, defaultValue: "ES-LUP-2400168 v1" },
	"end.footerDate": { order: 0, scenario: 9, defaultValue: "Fecha de elaboración: Septiembre 2024" },
	"scenario1.intro.title": { order: 1, scenario: 1, defaultValue: "Pruebas diagnósticas" },
	"scenario1.intro.question": { order: 2, scenario: 1, defaultValue: "¿Qué sabemos sobre la paciente?" },
	"scenario1.intro.description": { order: 3, scenario: 1, defaultValue: "Antes de que Martina entre en tu consulta, revisas la información que tienes sobre ella." },
	"scenario1.intro.paciente": { order: 4, scenario: 1, defaultValue: "Martina Ferrater" },
	"scenario1.intro.edad": { order: 5, scenario: 1, defaultValue: "37 años" },
	"scenario1.intro.historiaClinica.titulo": { order: 6, scenario: 1, defaultValue: "Historia Clínica" },
	"scenario1.intro.historiaClinica.contenido": {
		order: 7,
		scenario: 1,
		defaultValue: `<ul>
<li>No fumadora.</li>
<li>Bebedora de alcohol ocasional.</li>
<li>Anemia ferropénica, ferroterapia oral irregular.</li>
<li>Artritis en 1.ª y 2.ª articulaciones metacarpofalángicas derechas a los 31 años. Se realizaron pruebas y se trató con corticoides (no datos en historia clínica).</li>
<li>No problemas intestinales o gastrointestinales.</li>
<li>No cólicos reno ureterales ni infecciones del tracto urinario.</li>
<li>No embarazos ni abortos.</li>
<li>No bajo peso al nacer.</li>
</ul>
<p><strong>Antecedentes familiares</strong></p>
<ul>
<li>Madre con artritis reumatoide.</li>
<li>Hermana mayor con lupus eritematoso sistémico.</li>
</ul>
<p><strong>Tratamiento actual</strong></p>
<p>Sulfato ferroso 105 mg 1 comprimido/24 horas.</p>`,
	},
	"scenario1.intro.analiticaDerivacion.titulo": { order: 8, scenario: 1, defaultValue: "Analítica de derivación" },
	"scenario1.intro.analiticaDerivacion.contenido": {
		order: 9,
		scenario: 1,
		defaultValue: `<p><strong>Hb:</strong> 10,9 g/dl</p>
<p><strong>Leucocitos:</strong> 2070/µl</p>
<p><strong>Plaquetas:</strong> 178 000/µl</p>
<p><strong>Glucosa:</strong> 97 mg/dl</p>
<p><strong>Creatinina:</strong> 0,71 mg/dl</p>
<p><strong>FG:</strong> &gt; 90 ml/min/1,73 m²</p>
<p><strong>Urea:</strong> 22 mg/dl</p>
<p><strong>Ionograma:</strong> normal</p>
<p><strong>Proteína C reactiva:</strong> 7,2 mg/l</p>
<p><strong>Bioquímica hepática:</strong> normal</p>
<p><strong>Ferritina:</strong> 113 ng/ml</p>
<p><strong>Índice de saturación de transferrina:</strong> 27 %</p>
<p><strong>Sedimento de orina:</strong> leucocitos 10-15/campo, hematíes 15-20/campo, no bacterias/cilindros/cristales</p>
<p><strong>Tira reactiva (x 2):</strong> proteinuria 150-200 mg/dl</p>`,
	},
	"scenario1.conversationIntro.title": { order: 10, scenario: 1, defaultValue: "Ahora que Martina ya está en tu consulta, vas a hablar con ella." },
	"scenario1.conversationIntro.content": { order: 11, scenario: 1, defaultValue: "Presta atención a lo que te explica:" },
	"scenario1.conversation.messages": {
		order: 12,
		scenario: 1,
		defaultValue: JSON.stringify([
			{ speaker: "doctor", content: "Martina, la doctora Ramírez, que es su reumatóloga, la ha derivado a nefrología para que veamos cómo están sus riñones. <strong>Ahora le haré una pequeña revisión</strong>, pero antes me gustaría que me explicara un poco cómo se encuentra usted." },
			{ speaker: "patient", content: "Pues estoy regular, doctor, tengo una sensación de cansancio que no es normal. Además, desde hace 3 o 4 semanas, se me hinchan los tobillos, y es muy molesto." },
			{ speaker: "doctor", content: "¿Sangre en la orina ha tenido?" },
			{ speaker: "patient", content: "Sangre no, pero la orina sale como espumosa. Es muy raro." },
			{ speaker: "doctor", content: "¿Fiebre?" },
			{ speaker: "patient", content: "No, fiebre no." },
			{ speaker: "doctor", content: "¿Algún otro síntoma, como dolor en las articulaciones, caída del cabello...?" },
			{ speaker: "patient", content: "<p>¡Ay, doctor! El cabello sigue en su sitio, eso me faltaría. pero, ahora que lo dice, es verdad que a veces me duelen las muñecas y los codos… ¡Y eso que trabajo como fisioterapeuta!</p>" },
			{ speaker: "doctor", content: "<p>¿Ha notado si le cambian de color los dedos con el frío o el estrés?</p>" },
			{ speaker: "patient", content: "<p>No.</p>" },
			{ speaker: "doctor", content: "<p>¿Y más sensible de o normal a la luz? ¿O está como siempre?</p>" },
			{ speaker: "patient", content: "<p>Como siempre.</p>" },
			{ speaker: "doctor", content: "<p>¿Toma ibuprofeno o algún fármaco similar?</p>" },
			{ speaker: "patient", content: "<p>No, no suelo tomar calmantes a no ser que me lo indique mi médico.</p>" },
		]),
	},
	"scenario1.conversation.exploration": {
		order: 13,
		scenario: 1,
		defaultValue: `<div class="d-flex flex-column align-items-start justify-content-center">
<div class="txt-turquoise txt20">
	<strong>Realizas una exploración física a la paciente.</strong>
</div>
<div class="my-3">Estos son los resultados:</div>
<ul>
	<li>Altura: 165 cm, peso: 61 kg</li>
	<li>PA en consulta externa: 142/92 mmHg</li>
	<li>FC: 78 lpm </li>
	<li>
		Afebril, buen estado general. Consciente y orientada,
		colaboradora. Normocoloreada y normohidratada.
	</li>
	<li>Torácica cerrada y resonante, sin soplos.</li>
	<li>Normoperfundida, edema ++ pretibial.</li>
	<li>No lesiones cutáneas.</li>
	<li>
		Eupneica en reposo, movimientos coordinados voluntarios, sin
		ruidos sobreañadidos.
	</li>
	<li>
		Sin focalidad neurológica ni <i>flapping</i>.
	</li>
</ul>
</div>`,
	},
	"scenario1.conversation.historicalTable": {
		order: 14,
		scenario: 1,
		defaultValue: `<p style="text-align:center;"><span style="color:hsl(240,75%,60%);font-size:24px;"><strong>Finalmente, entre la documentación observas que hay un histórico de análisis.</strong></span></p><p style="text-align:center;"><span style="font-size:16px;">Échale un vistazo por si acaso…</span></p><p style="text-align:center;"><span style="color:hsl(240,75%,60%);font-size:20px;"><strong>Histórico de analíticas&nbsp;</strong></span><span style="font-size:20px;"> &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;</span><span style="color:hsl(240,75%,60%);font-size:20px;"> Paciente: Martina Ferrater</span></p><figure class="table"><table><tbody><tr><th>&nbsp;</th><th>09-06-2018</th><th>10-07-2018</th><th>14-10-2021</th><th>08-09-2022</th><th>04-10-2023</th></tr></tbody></table></figure><figure class="table"><table><tbody><tr><td><span style="color:hsl(240,75%,60%);"><strong>Hb (g/dl)</strong></span></td><td>13,7</td><td>12,8</td><td><span style="color:hsl(0,75%,60%);"><strong>11,9</strong></span></td><td><span style="color:hsl(0,75%,60%);"><strong>11</strong></span></td><td><span style="color:hsl(0,75%,60%);"><strong>10,9</strong></span></td></tr><tr><td><span style="color:hsl(240,75%,60%);"><strong>Leucocitos (/µL)</strong></span></td><td>6715</td><td>7290</td><td><span style="color:hsl(0,75%,60%);"><strong>3210</strong></span></td><td><span style="color:hsl(0,75%,60%);"><strong>2413</strong></span></td><td><span style="color:hsl(0,75%,60%);"><strong>2050</strong></span></td></tr><tr><td><span style="color:hsl(240,75%,60%);"><strong>Plaquetas (/µL)</strong></span></td><td>218 000</td><td>197 000</td><td>192 000</td><td>183 000</td><td>178 000</td></tr><tr><td><span style="color:hsl(240,75%,60%);"><strong>Creatinina (mg/dl)</strong></span></td><td>0,81</td><td>0,87</td><td>0,79</td><td>0,8</td><td>0,71</td></tr><tr><td><span style="color:hsl(240,75%,60%);"><strong>Leucocitos orina</strong></span></td><td>0</td><td>0</td><td>0</td><td>0</td><td><span style="color:hsl(0,75%,60%);"><strong>10-15/c</strong></span></td></tr><tr><td><span style="color:hsl(240,75%,60%);"><strong>Hematíes orina</strong></span></td><td>0</td><td>1-5/c</td><td>0</td><td>0</td><td><span style="color:hsl(0,75%,60%);"><strong>15-20/c</strong></span></td></tr><tr><td><span style="color:hsl(240,75%,60%);"><strong>UPCR (mg/g)</strong></span></td><td>12,3</td><td>46</td><td>150</td><td>345</td><td><span style="color:hsl(0,75%,60%);"><strong>3700</strong></span></td></tr><tr><td><span style="color:hsl(240,75%,60%);"><strong>ANA</strong></span></td><td>&nbsp;</td><td><strong>Positivo</strong></td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr><tr><td><span style="color:hsl(240,75%,60%);"><strong>Factor reumatoide</strong></span></td><td>&nbsp;</td><td><strong>Negativo</strong></td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr></tbody></table></figure>`,
	},
	"scenario1.conversation.questionInfo": {
		order: 15,
		scenario: 1,
		// Solo la parte editable. La puntuación (+10 justificable / -20 injustificada) es fija en la web y no se edita aquí.
		defaultValue: `<div class="d-flex flex-column align-items-start justify-content-center">
<div class="txt-turquoise bold txt20 text-center">
	<strong>Tras revisar toda la información relacionada con esta paciente,
	¿qué pruebas solicitarías?</strong>
</div>
<div class="d-flex align-items-start mt-4">
	<ul>
		<li>
			En la siguiente batería de pruebas, hay 3 que no se
			justifican, y 6 que pueden ser justificables. Selecciona
			solo las 6 pruebas justificables y lee con atención los
			resultados.
		</li>
		<li class="mt-2">
			Avanzarás cuando hayas seleccionado las 6 pruebas
			correctas.
		</li>
	</ul>
</div>`,
	},
	"scenario2.intro.title": { order: 16, scenario: 2, defaultValue: "Diagnóstico" },
	"scenario2.enunciado.content": {
		order: 17,
		scenario: 2,
		defaultValue: "A la vista de los resultados de las pruebas y de este informe, ¿qué diagnóstico te parece más probable?",
	},
	"scenario2.result.content": {
		order: 18,
		scenario: 2,
		defaultValue: "<p style=\"text-align:center;\">Así pues, el diagnóstico de Martina es:&nbsp;</p><p style=\"text-align:center;\"><span style=\"color:hsl(90, 75%, 60%);\"><strong>Nefritis lúpica clase IV Índice de actividad: 12/24; índice de cronicidad: 1/12</strong></span></p><p style=\"text-align:center;\">Ahora hay que valorar un tratamiento para la paciente. Pero antes…</p>",
	},
	"scenario3.intro.title": { order: 19, scenario: 3, defaultValue: "Diagnóstico por imagen" },
	"scenario3.intro.content": { order: 20, scenario: 3, defaultValue: "Recibes un correo electrónico de un colega:" },
	"scenario3.intro.modalTitle": { order: 21, scenario: 3, defaultValue: "Aquí tienes una batería de 4 imágenes y 4 etiquetas." },
	"scenario3.intro.modalList": {
		order: 22,
		scenario: 3,
		defaultValue: "<li>Relaciónalas correctamente.</li><li class=\"mt-2\">Cuando las 4 imágenes tengan una etiqueta asignada, pulsa <strong style=\"color: #FF7F6B\">Confirmar</strong> y comprueba tus aciertos.</li><li>Las imágenes con la descripción correcta se quedarán fijas.</li><li>Debes acertar las 4 imágenes para superar la prueba.</li>",
	},
	"scenario3.result.content": {
		order: 23,
		scenario: 3,
		defaultValue: "¡Ahora ya puedes seguir con <br /> el caso de Martina!",
	},
	"scenario4.intro.title": { order: 24, scenario: 4, defaultValue: "Tratamiento" },
	"scenario4.intro.content": {
		order: 25,
		scenario: 4,
		defaultValue: "<div class=\"bold mb-3\"><strong>Recuerda:</strong></div><ul><li>Martina, 37 años.</li><li>Lupus eritematoso sistémico sin manifestaciones extrarrenales actualmente, no tratamiento.</li><li>Nefritis lúpica clase IV, filtrado glomerular conservado, proteinuria 3070 mg/24 h, microhematuria y leucocituria.</li><li>Anticuerpos contra el ADN de doble cadena + consumo de complemento (C3-C4).</li></ul>",
	},
	"scenario4.question": { order: 26, scenario: 4, defaultValue: "¿Cuál crees que es el mejor tratamiento para la paciente en este momento?" },
	"scenario4.subtitle": { order: 27, scenario: 4, defaultValue: "Selecciona el tratamiento más adecuado" },
	"scenario4.option1.label": { order: 28, scenario: 4, defaultValue: "Doble terapia" },
	"scenario4.option1.content1": {
		order: 29,
		scenario: 4,
		defaultValue: "<ul><li class=\"mt-3\">Bolo de metilprednisolona 0,25-0,5 g/día durante 3 días (y posterior pauta descendente)</li><li class=\"mt-2\">Micofenolato de mofetilo 2-3 g/día</li><li class=\"mt-2\">Hidroxicloroquina a ≤5 mg/kg/día (hasta un máximo de 400 mg/d)</li></ul>",
	},
	"scenario4.option1.content2Title": {
		order: 30,
		scenario: 4,
		defaultValue: "Regímenes o pautas de corticoides para pacientes con nefritis lúpica",
	},
	"scenario4.option1.content2Citation": {
		order: 31,
		scenario: 4,
		defaultValue: "Rojas-Rivera JE, García-Carro C, Ávila A, et al. Documento de consenso del Grupo de Estudio de Enfermedades Glomerulares de la Sociedad Española de Nefrología (GLOSEN) para el diagnóstico y tratamiento de la nefritis lúpica. Nefrología. 2023;43:6-47.",
	},
	"scenario4.option2.label": { order: 32, scenario: 4, defaultValue: "Triple terapia" },
	"scenario4.option2.content1": {
		order: 33,
		scenario: 4,
		defaultValue: "<ul><li class=\"mt-3\">Bolo de metilprednisolona 0,25-0,5 g/día durante 2 días (y posterior pauta descendente)</li><li class=\"mt-2\">Micofenolato de mofetilo 2 g/día</li><li class=\"mt-2\">Hidroxicloroquina a ≤5 mg/kg/día (hasta un máximo de 400 mg/d)</li><li class=\"mt-2\">Voclosporina: 23,7 mg/12 h</li></ul>",
	},
	"scenario4.option2.content2Title": {
		order: 34,
		scenario: 4,
		defaultValue: "Esquema de reducción de glucocorticoides",
	},
	"scenario4.option2.content2Citation": {
		order: 35,
		scenario: 4,
		defaultValue: "Nota: las dosis de corticoides definidas por protocolo dependían del peso de los pacientes (≥45 kg y <45kg). Se grafican sólo las dosis para pacientes ≥45 kg. En la parte superior de la diapositiva se incluyen todas las dosis con independencia del peso<br />Rovin BH, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021 May 29;397:2070-2080",
	},
	"scenario4.modalLine1": {
		order: 36,
		scenario: 4,
		defaultValue: "<p>&nbsp;</p><p><span style=\"font-size:18px;\"><strong>Has elegido prescribir&nbsp;</strong></span></p><p><span style=\"color:hsl(240,75%,60%);font-size:18px;\"><strong>{{OPTION}}</strong></span><span style=\"font-size:18px;\">.</span></p>",
	},
	"scenario4.modalLine2": { order: 37, scenario: 4, defaultValue: "<p class=\"txt-black\">Veamos cómo evoluciona Martina.</p>" },
	"scenario5.intro.title": { order: 38, scenario: 5, defaultValue: "Actitud terapéutica" },
	"scenario5.intro.content": {
		order: 39,
		scenario: 5,
		defaultValue: "<p><strong>Tres meses más tarde,&nbsp;</strong><br><strong>Martina acude a tu consulta.</strong></p><p>Antes de que entre, revisas los resultados de sus analíticas. Obsérvalos con atención.</p><p>Consumo C3 (mes 1 – mes 3): 12 mg/dl&nbsp;<br>Consumo C4 (mes 1 – mes 3): 2 mg/dl&nbsp;</p>",
	},
	"scenario5.conversation.messages": {
		order: 40,
		scenario: 5,
		defaultValue: JSON.stringify([
			{ speaker: "doctor", content: "Martina, han pasado tres meses desde que iniciamos el tratamiento. ¿Cómo se encuentra?" },
			{ speaker: "patient", content: "Me encuentro mejor, doctor. El cansancio ha disminuido y la hinchazón de tobillos también." },
			{ speaker: "doctor", content: "Hemos revisado sus analíticas de estos meses. Los resultados son favorables." },
			{ speaker: "patient", content: "¿Qué tal han ido los análisis?" },
			{ speaker: "doctor", content: "El consumo de complemento C3 y C4 ha mejorado respecto al inicio. Eso indica buena respuesta al tratamiento." },
			{ speaker: "patient", content: "¿Y qué hacemos a partir de ahora?" },
			{ speaker: "doctor", content: "El documento de consenso GLOSEN 2023 propone unos objetivos terapéuticos a los 2-3 meses. ¿Conoce estos objetivos?" },
			{ speaker: "patient", content: "No los conozco bien, doctor. ¿Me los puede explicar?" },
		]),
	},
	"scenario5.conversation.explanationModal": {
		order: 41,
		scenario: 5,
		defaultValue: "<div class=\"text-center\"><div class=\"txt-black txt16\">El <span class=\"bold\">documento de consenso GLOSEN 2023</span> propone una serie de objetivos terapéuticos que se deben alcanzar a los 2-3 meses del inicio del tratamiento para la nefritis lúpica, tanto en primera línea de tratamiento como en recaída.</div><div class=\"mt-3 txt-turquoise bold txt18\">¿Conoces estos objetivos?</div></div>",
	},
	"scenario5.explanation.instruction": {
		order: 42,
		scenario: 5,
		defaultValue: "Fíjate bien en este gráfico y avanza para responder la pregunta.",
	},
	"scenario5.explanation.buttonText": {
		order: 43,
		scenario: 5,
		defaultValue: "IR A LA PREGUNTA",
	},
	"scenario5.question": {
		order: 44,
		scenario: 5,
		defaultValue: "¿Qué criterios a los tres meses propone el documento de consenso GLOSEN 2023 para valorar la efectividad de un tratamiento de debut o recaída de nefritis lúpica?",
	},
	"scenario5.footer": {
		order: 45,
		scenario: 5,
		defaultValue: "1 Rojas-Rivera JE, García-Carro C, Ávila A, et al. Documento de consenso del Grupo de Estudio de Enfermedades Glomerulares de la Sociedad Española de Nefrología (GLOSEN) para el diagnóstico y tratamiento de la nefritis lúpica. Nefrología. 2023;43:6-47<br/> <strong>eGFR:</strong> tasa de filtración glomerular estimada",
	},
	"scenario5.questionFootnote": {
		order: 46,
		scenario: 5,
		defaultValue: "Como curiosidad, la respuesta b) correspondía a los criterios propuestos para los 6 meses.",
	},
	"scenario5.end.title": {
		order: 47,
		scenario: 5,
		defaultValue: "Parece evidente que hay que modificar<br/>el tratamiento a Martina.",
	},
	"scenario5.end.content": {
		order: 48,
		scenario: 5,
		defaultValue: "De hecho, aunque a Martina se le ha prescrito un tratamiento con doble terapia, teniendo en cuenta sus datos y las recomendaciones de las guías, <span class=\"bold\">se podría haber iniciado un tratamiento de primera línea directamente con triple terapia</span>. Veamos el algoritmo que propone el documento de consenso GLOSEN 2023<sup>1</sup>",
	},
	"scenario5.2.intro2.title": {
		order: 49,
		scenario: 5,
		defaultValue: "Uno de los fármacos que puedes introducir para la triple terapia es <span class=\"txt-yellow-light\">voclosporina</span>.",
	},
	"scenario5.2.intro2.subtitle": {
		order: 50,
		scenario: 5,
		defaultValue: "¿Has oído hablar de este nuevo inhibidor de la calcineurina? ¿Y del estudio Aurora 1?",
	},
	"scenario5.2.intro2.caption": {
		order: 51,
		scenario: 5,
		defaultValue: "Figura adaptada de Huizinga et al. The 12th International Congress on Systemic Lupus Erythematosus (LUPUS 2017) & the 7th Asian Congress on Autoimmunity (ACA 2017).",
	},
	"scenario5.2.intro2.imageCitation": {
		order: 52,
		scenario: 5,
		defaultValue: "<sup>1</sup>Busque S et al. Am J Transplant 2011;11:2675–84. Rovin BH et al. Data presented at American Society of Nephrology Kidney Week 2020 Reimagined virtual conference, 19–25 October 2020. Kuglstatter A et al. Acta Crystallogr D Biol Crystallogr. 2011;67:119‒23. Van Gelder T et al. Expert Rev Clin Pharmacol. 2022;15:515‒29.",
	},
	"scenario5.2.intro2.modalContent": {
		order: 53,
		scenario: 5,
		defaultValue: "<p style=\"text-align:center;\"><span style=\"color:hsl(210, 75%, 60%);\"><strong>El estudio Aurora 1<sup>1</sup> es un ensayo clínico aleatorizado, multicéntrico, controlado por placebo y doble ciego, N = 357, cuya variable principal fue respuesta renal completa a la semana 52 con n=357 pacientes con nefritis lúpica.</strong></span></p><p style=\"text-align:center;\">A continuación, encontrarás una batería de 4 afirmaciones.</p><p style=\"text-align:center;\"><strong>¿Son verdaderas o falsas?</strong></p><p><span style=\"font-size:12px;\"><sup>1</sup> Rovin BH, Onno Teng YK, Ginzler EM, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021;397:2070-80.</span></p>",
	},
	"scenario5.2.questionInfo.enunciado": {
		order: 54,
		scenario: 5,
		defaultValue: "Todas las afirmaciones siguientes sobre voclosporina son ciertas, excepto una. ¿Sabías identificar cuál es la «afirmación infiltrada»?",
	},
	"scenario5.2.instruction": {
		order: 55,
		scenario: 5,
		defaultValue: "Indica si estas afirmaciones relacionadas con el estudio Aurora<sup>1</sup> son verdaderas (V) o falsas (F):",
	},
	"scenario5.2.citation": {
		order: 56,
		scenario: 5,
		defaultValue: "<sup>1</sup>Rovin BH, Onno Teng YK, Ginzler EM, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021;397:2070-80.",
	},
	"scenario5.2.footer": {
		order: 57,
		scenario: 5,
		defaultValue: "RRC: respuesta renal completa<br/>UPCR: cociente proteína/creatinina en orina",
	},
	"scenario5.2.2.instruction": {
		order: 58,
		scenario: 5,
		defaultValue: "Indica cuál es la \"afirmación infiltrada\". Tienes tantos intentos como sea preciso.",
	},
	"scenario5.2.2.citation": {
		order: 59,
		scenario: 5,
		defaultValue: "Rovin BH, Onno Teng YK, Ginzler EM, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021;397:2070-80.",
	},
	"scenario5.2.2.modalCorrect.title": {
		order: 60,
		scenario: 5,
		defaultValue: "¡Efectivamente, esta es la afirmación infiltrada!"
	},
	"scenario5.2.2.modalCorrect.content": {
		order: 61,
		scenario: 5,
		defaultValue: `

			<div>En su estudio pivotal AURORA 1<sup>1</sup>:</div>

			<div>· Voclosporina demostró tener un <span class="bold">efecto favorable</span> sobre el <span class="bold">metabolismo lipídico</span>, reduciendo de forma estadísticamente significativa el colesterol total y las LDL.</div>

			<div>· Voclosporina <span class="bold">no tuvo impacto</span> en la <span class="bold">hemoglobina glicosilada</span> ni en las <span class="bold">concentraciones de glucosa sérica</span>, que se mantuvieron estables a lo largo de todo el estudio.</div>

			<div>· Solo se observó <span class="bold">diabetes de novo</span> en un paciente del <span class="bold">grupo placebo</span>.</div>

			<div>· Los <span class="bold">electrolitos</span> y las <span class="bold">concentraciones medias de magnesio y potasio</span> se mantuvieron <span class="bold">estables</span> para ambos brazos de tratamiento.</div>

			<div class="txt8 mt-3">
			<sup>1</sup>Rovin BH, Onno Teng YK, Ginzler EM, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021;397:2070-80.
			</div>
		`,
		},

	"scenario5.2.end.enunciado": {
		order: 62,
		scenario: 5,
		defaultValue: "Finalmente te decantas por incluir voclosporina en el tratamiento de Martina, especialmente porque se trata de un fármaco oral que reduce la proteinuria de manera más rápida que el tratamiento estándar y que no requiere monitorización de niveles plasmáticos.",
	},
	"scenario5.2.end.tratamiento": {
		order: 63,
		scenario: 5,
		defaultValue: "Ahora Martina sigue este tratamiento:",
	},
	"scenario6-intro.conversation.messages": {
		order: 62,
		scenario: 6,
		defaultValue: JSON.stringify([
			{ speaker: "doctor", content: "¿Sí?" },
			{ speaker: "doctora", content: "Gabriel, soy Aitana, de Reumatología. ¿Te pillo en buen momento?" },
			{ speaker: "doctor", content: "Sí, Aitana: dime" },
			{ speaker: "doctora", content: "Te llamo para consultarte sobre Martina, la paciente con nefritis lúpica...¿Te acuerdas de ella?" },
			{ speaker: "doctor", content: "Sí, sí, claro. Hace un mes le cambié el tratamiento. ¿Qué le sucede?" },
			{ speaker: "doctora", content: "Verás, es que presenta un cuadro de gastroenteritis aguda y prefiero consultar contigo el tema del tratamiento..." },
			{ speaker: "doctor", content: "¡Vaya por Dios! ¿Tiene fiebre?" },
			{ speaker: "doctora", content: "No, su exploración ha sido normal... con una temperatura de 36,3. Pero ahora le ha subido la creatinina a 1,25" },
			{ speaker: "doctor", content: "Oye, pásame los datos que tengas y valoro si hay que hacer ajuste de dosis, ¿te parece bien?" },
			{ speaker: "doctora", content: "Estupendo, Gabriel. Ahora te envío la info." },
		]),
	},
	"scenario6-intro.enunciado-title": {
		order: 63,
		scenario: 6,
		defaultValue: "Ajuste de dosis"
	},
	"scenario6-intro.enunciado": {
		order: 64,
		scenario: 6,
		defaultValue: "Ha transcurrido un mes desde la última visita de Martina. <br> Estás en tu consulta cuando recibes una llamada."
	},
	"scenario6-intro.test-title": {
		order: 65,
		scenario: 6,
		defaultValue: "¿Sabes cómo ajustar voclosporina en función de la disminución de la tasa de filtración glomerular estimada (TFGe)?<br> Relaciona los valores de disminución confirmada de la TFGe desde el inicio con las recomendaciones de dosificación de voclosporina. Pulsa <span class=\"bold\">Confirmar</span> para comprobar si has acertado."
	},
	"scenario6-intro.informe-title": {
		order: 66,
		scenario: 6,
		defaultValue: "Informe"
	},
	"scenario6-intro.informe": {
		order: 67,
		scenario: 6,
		defaultValue: "Al inicio del tratamiento, Martina tenía un TFGe de 90 ml/min/1,73 m<sup>2</sup> y en la última analítica era de 59. Su reducción ha sido del 34,4 %."
	},
	// Scenario 6 (título + contenido: ficha técnica y leyenda TFGe/BID)
	"scenario6.title": {
		order: 68,
		scenario: 6,
		defaultValue: "Disminución confirmada de la TFGe con respecto al valor inicial"
	},
	"scenario6.content": {
		order: 69,
		scenario: 6,
		defaultValue: "Ver ficha técnica de voclosporina. Si la TFGe sigue siendo ≥ 60 ml/minuto/1,73 m2 no es necesario adoptar medidas.\n\nTFGe: tasa de filtración glomerular estimada\nBID: dos veces al día"
	},
	"scenario6.recommendation-title": {
		order: 70,
		scenario: 6,
		defaultValue: "Recomendación de dosificación:"
	},
	"scenario6.modal-final": {
		order: 71,
		scenario: 6,
		defaultValue: "<div class=\"text-center txt14 txt-black\"><div class=\"bold txt-turquoise txt18\">¡Excelente, todas las recomendaciones son correctas!</div><div class=\"mt-3\">Disminución confirmada de la TFGe desde el inicio:</div><div class=\"mt-3\"><span class=\"txt-dark-green bold\">≥30 %</span> -> Suspender la administración de voclosporina y reiniciar el tratamiento tras la recuperación de la TFGe con una dosis de 7,9 mg (1 cápsula) 2 veces al día y aumentar la dosis según la tolerabilidad de la función renal.</div><div class=\"mt-3\"><span class=\"txt-dark-green bold\">>20 % y <30 %</span> -> Reducir la dosis de voclosporina en 7,9 mg (1 cápsula) 2 veces al día. Volver a hacer la prueba en dos semanas; si la disminución de la TFGe no se ha recuperado, volver a reducir la dosis en 7,9 mg (1 cápsula) 2 veces al día.</div><div class=\"mt-3\"><span class=\"txt-dark-green bold\">≤20 %</span> -> Mantener la dosis actual de voclosporina y supervisar.</div><div class=\"mt-3\">En ninguno de estos supuestos es necesario suspender definitivamente voclosporina.</div><div class=\"bold txt-light-green mt-4 txt18\">¡Ahora ya sabes cómo ajustar el tratamiento de Martina!</div></div>"
	},
	// Scenario 6-end (pantalla final)
	"scenario6-end.intro": {
		order: 72,
		scenario: 6,
		defaultValue: "Al inicio del tratamiento, Martina tenía un TFGe de 90 ml/min/1,73 m<sup>2</sup> y en la última analítica era de 59. Su reducción ha sido del 34,4 %. Por lo tanto, hay que:"
	},
	"scenario6-end.steps": {
		order: 73,
		scenario: 6,
		defaultValue: "1. Suspender temporalmente la administración de voclosporina.\n2. Reiniciar el tratamiento tras la recuperación de la TFGe con una dosis de 7,9 mg (1 cápsula) 2 veces al día y aumentar la dosis según la tolerabilidad de la función renal."
	},
	// Scenario 7-intro
	"scenario7-intro.title": {
		order: 74,
		scenario: 7,
		defaultValue: "Tiempo de tratamiento"
	},
	"scenario7-intro.subtitle": {
		order: 75,
		scenario: 7,
		defaultValue: "Han pasado 2 meses más"
	},
	"scenario7-intro.body": {
		order: 76,
		scenario: 7,
		defaultValue: "Martina ya se recuperó de su gastroenteritis aguda y ha vuelto a la normalización de la dosis de voclosporina y de su función renal. <div class=\"my-4\">Ahora la pregunta es:</div><div class=\"txt-yellow bold txt20\">¿Durante cuánto tiempo más debería mantenerse el tratamiento?</div><div class=\"mt-4 txt-yellow bold txt20\">¿Qué dicen las guías al respecto?</div>"
	},
	"scenario7-intro.instruction": {
		order: 77,
		scenario: 7,
		defaultValue: "Indica la duración del tratamiento recomendada por la guía GLOSEN 2023<sup>1</sup>."
	},
	"scenario7-intro.citation": {
		order: 78,
		scenario: 7,
		defaultValue: "<sup>1</sup>Rojas-Rivera JE, García-Carro C, Ávila A, et al. Documento de consenso del Grupo de Estudio de Enfermedades Glomerulares de la Sociedad Española de Nefrología (GLOSEN) para el diagnóstico y tratamiento de la nefritis lúpica. Nefrología. 2023;43:6-47."
	},
	// Scenario 7 (pantalla principal: duración tratamiento)
	"scenario7.instruction": {
		order: 79,
		scenario: 7,
		defaultValue: "Indica la duración del tratamiento recomendada por la guía GLOSEN 2023."
	},
	"scenario7.explanation": {
		order: 80,
		scenario: 7,
		defaultValue: "<div class=\"mt-4\">Esta es la recomendación de la guía GLOSEN 2023<sup>1</sup> sobre la duración del tratamiento:</div><div class=\"mt-2\"><span class=\"bold\">Análogos del ácido micofenólico (MPAA):</span> Continuar como tratamiento de mantenimiento (3-5 años).</div><div class=\"mt-2\"><span class=\"bold\">Ciclofosfamida:</span> 3 meses de tratamiento con MPAA de mantenimiento.</div><div class=\"mt-2\"><span class=\"bold\">Inhibidores de la calcineurina (ICN):</span> 12-18 meses, luego 6-12 meses de retirada gradual.</div><div class=\"mt-4 txt10\"><sup>1</sup>Rojas-Rivera JE, García-Carro C, Ávila A, et al. Documento de consenso del Grupo de Estudio de Enfermedades Glomerulares de la Sociedad Española de Nefrología (GLOSEN) para el diagnóstico y tratamiento de la nefritis lúpica. Nefrología. 2023;43:6-47.</div>"
	},
	"scenario7-end.content": {
		order: 81,
		scenario: 7,
		defaultValue: "Vista esta información, decides mantener el tratamiento de Martina durante 3 años.<br/><br/>¡Casi hemos llegado al final del caso! Te queda solo la última de las pruebas. ¿Serás capaz de superar el supertest?"
	},
	"scenario7.2.title": {
		order: 82,
		scenario: 7,
		defaultValue: "¿Qué recomiendan otras sociedades científicas acerca de la duración del tratamiento?"
	},
	"scenario7.3.title": {
		order: 82,
		scenario: 7,
		defaultValue: "¿Qué recomiendan otras sociedades científicas acerca de la duración del tratamiento?"
	},
	// Supertest (scenario 8 en filtro)
	"supertest-intro.title": {
		order: 83,
		scenario: 8,
		defaultValue: "¿Quieres añadir 50 puntos a tu marcador? Responde correctamente a las 3 preguntas del supertest."
	},
	"supertest-intro.content": {
		order: 84,
		scenario: 8,
		defaultValue: "Solo tienes un intento por pregunta y solo ganarás los puntos si respondes correctamente las 3 preguntas."
	},
	"supertest.title": {
		order: 85,
		scenario: 8,
		defaultValue: "¿Cuál de las siguientes afirmaciones NO es correcta?"
	},
	"supertest.content": {
		order: 86,
		scenario: 8,
		defaultValue: "NL: nefritis lúpica; LES: lupus eritematoso sistémico"
	},
	"supertest.result.titleSuccess": {
		order: 87,
		scenario: 8,
		defaultValue: "¡Enhorabuena! ¡Has acertado todas las respuestas!"
	},
	"supertest.result.titleFail": {
		order: 88,
		scenario: 8,
		defaultValue: "¡Vaya! Has fallado al menos una de las respuestas…"
	},
	"supertest.result.subtitleSuccess": {
		order: 89,
		scenario: 8,
		defaultValue: "+50 puntos"
	},
	"supertest.result.subtitleFail": {
		order: 90,
		scenario: 8,
		defaultValue: "He aquí las justificaciones"
	},
	"supertest.result.references": {
		order: 91,
		scenario: 8,
		defaultValue: "<sup>1</sup> 1 Rojas-Rivera JE, Praga Terente M. Nefropatía Lúpica. Lupus Eritematoso Sistémico. En: Lorenzo V, López Gómez JM (eds). Nefrología al día. Disponible en: https://www.nefrologiaaldia.org/589 [última consulta: 5 marzo 2024].<br/><sup>2</sup> Rovin BH, Onno Teng YK, Ginzler EM, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021;397:2070-80. <br/><sup>3</sup> Saxena A, Ginzler EM, Gibson K, et al. Safety and efficacy of long-Term voclosporin treatment for lupus nephritis in the phase 3 AURORA 2 clinical trial. Arthritis Rheumatol. 2024;76(1):59-67.<br/><sup>4</sup> Arriens C, Onno Teng YK, Ginzler EM, et al. Update on the efficacy and safety profile of voclosporin: an integrated analysis of clinical trials in lupus nephritis. Arthritis Care Res (Hoboken). 2023;75(7):1399-1408.<br/><strong>NL:</strong> nefritis lúpica"
	},
	"supertest.justification.title": {
		order: 92,
		scenario: 8,
		defaultValue: "<div class=\"mt-3\"><span class=\"bold\">Pregunta 1:</span> todas las afirmaciones son correctas. Cabe destacar que la NL se caracteriza por la aparición de proteinuria en grado variable (40-100 %)<sup>1</sup>, alteraciones en el sedimento urinario (hematuria hasta en el 80 %), deterioro de la función renal (40-80%) y en algunos casos, desarrollo de hipertensión arterial (15-50 %)<sup>1</sup>. Además, el control adecuado de tensión arterial y de la dislipemia, frecuente en los pacientes con NL, son importantes para prevenir eventos cardiovasculares<sup>1</sup>.</div><div class=\"mt-2\"><span class=\"bold\">Pregunta 2:</span> voclosporina ha demostrado ser eficaz en nefritis lúpica de nuevo diagnóstico y en recaídas y en varios perfiles de pacientes, incluidos pacientes con NL de clase III<sup>2-4</sup>.</div><div class=\"mt-2\"><span class=\"bold\">Pregunta 3:</span> la afirmación es verdadera: la preservación de la función renal a largo plazo, junto con los resultados de seguridad favorables del estudio Aurora 2, establecen un perfil beneficio-riesgo positivo para voclosporina como parte del tratamiento estándar de NL durante 3 años<sup>3</sup>.</div>"
	},
	"supertest.justification.content": {
		order: 93,
		scenario: 8,
		defaultValue: "<sup>1</sup> Rojas-Rivera JE, Praga Terente M. Nefropatía Lúpica. Lupus Eritematoso Sistémico. En: Lorenzo V, López Gómez JM (eds). Nefrología al día. Disponible en: https://www.nefrologiaaldia.org/589 [última consulta: 5 marzo 2024].<br/><sup>2</sup> Rovin BH, Onno Teng YK, Ginzler EM, et al. Efficacy and safety of voclosporin versus placebo for lupus nephritis (AURORA 1): a double-blind, randomised, multicentre, placebo-controlled, phase 3 trial. Lancet. 2021;397:2070-80.<br/><sup>3</sup> Saxena A, Ginzler EM, Gibson K, et al. Safety and efficacy of long-Term voclosporin treatment for lupus nephritis in the phase 3 AURORA 2 clinical trial. Arthritis Rheumatol. 2024;76(1):59-67.<br/><sup>4</sup> Arriens C, Onno Teng YK, Ginzler EM, et al. Update on the efficacy and safety profile of voclosporin: an integrated analysis of clinical trials in lupus nephritis. Arthritis Care Res (Hoboken). 2023;75(7):1399-1408.<br/><strong>NL:</strong> nefritis lúpica"
	},
	"supertest-end.title": {
		order: 94,
		scenario: 8,
		defaultValue: "Por cierto… ¿Qué fue de Martina?"
	},
	"supertest-end.content": {
		order: 95,
		scenario: 8,
		defaultValue: "Martina siguió con su medicación hasta cumplir 3 años de tratamiento. Dada la situación de remisión clínica e inmunológica, se decide iniciar descenso progresivo del tratamiento inmunosupresor y control prospectivo de la actividad del lupus y la función renal."
	},
};

// Cada sección tiene label, opcionalmente titleKey y/o contentKey (dentro de esa sección).
const SECTIONS = [
	{ label: "FT - Ficha técnica (título y contenido)", order: -2, scenario: 0, titleKey: "FT.title", contentKey: "FT.content" },
	{ label: "Context", order: 0, scenario: 0, titleKey: null, contentKey: "context.intro" },
	{ label: "Context - Modal empezar juego", order: 0, scenario: 0, titleKey: null, contentKey: "context.startGameModal" },
	{ label: "Intro - Pregunta", order: 1, scenario: 1, titleKey: "scenario1.intro.title", contentKey: "scenario1.intro.question" },
	{ label: "Intro - Descripción", order: 2, scenario: 1, titleKey: null, contentKey: "scenario1.intro.description" },
	{ label: "Paciente", order: 3, scenario: 1, titleKey: null, contentKey: "scenario1.intro.paciente" },
	{ label: "Edad", order: 4, scenario: 1, titleKey: null, contentKey: "scenario1.intro.edad" },
	{ label: "Historia Clínica", order: 5, scenario: 1, titleKey: "scenario1.intro.historiaClinica.titulo", contentKey: "scenario1.intro.historiaClinica.contenido" },
	{ label: "Analítica de derivación", order: 6, scenario: 1, titleKey: "scenario1.intro.analiticaDerivacion.titulo", contentKey: "scenario1.intro.analiticaDerivacion.contenido" },
	{ label: "Conversación - Intro", order: 7, scenario: 1, titleKey: "scenario1.conversationIntro.title", contentKey: "scenario1.conversationIntro.content" },
	{ label: "Conversación (escenario 1)", order: 8, scenario: 1, titleKey: null, contentKey: "scenario1.conversation.messages", type: "conversation" },
	{ label: "Conversación - Modal 1 (Exploración física)", order: 9, scenario: 1, titleKey: null, contentKey: "scenario1.conversation.exploration" },
	{ label: "Conversación - Modal 2 (Histórico análisis)", order: 10, scenario: 1, titleKey: null, contentKey: "scenario1.conversation.historicalTable" },
	{ label: "Conversación - Modal 3 (Pregunta e instrucciones; +10/-20 pts fijo)", order: 11, scenario: 1, titleKey: null, contentKey: "scenario1.conversation.questionInfo" },
	{ label: "Intro escenario 2", order: 0, scenario: 2, titleKey: "scenario2.intro.title", contentKey: "scenario2.intro.content" },
	{ label: "Escenario 2 enunciado (escoger diagnostico)", order: 1, scenario: 2, titleKey: null, contentKey: "scenario2.enunciado.content" },
	{ label: "Scenario2 result", order: 2, scenario: 2, titleKey: null, contentKey: "scenario2.result.content" },
	{ label: "Intro escenario 3", order: 0, scenario: 3, titleKey: "scenario3.intro.title", contentKey: "scenario3.intro.content" },
	{ label: "Intro escenario 3 - Modal instrucciones", order: 1, scenario: 3, titleKey: "scenario3.intro.modalTitle", contentKey: "scenario3.intro.modalList" },
	{ label: "Scenario3 result", order: 2, scenario: 3, titleKey: null, contentKey: "scenario3.result.content" },
	{ label: "Intro escenario 4", order: 0, scenario: 4, titleKey: "scenario4.intro.title", contentKey: "scenario4.intro.content" },
	{ label: "Scenario4 - Pregunta tratamiento", order: 1, scenario: 4, titleKey: null, contentKey: "scenario4.question" },
	{ label: "Scenario4 - Instrucción", order: 2, scenario: 4, titleKey: null, contentKey: "scenario4.subtitle" },
	{ label: "Scenario4 - Opción 1 (Doble terapia) - Título", order: 3, scenario: 4, titleKey: "scenario4.option1.label", contentKey: "scenario4.option1.content1" },
	{ label: "Scenario4 - Opción 1 - Esquema título y cita", order: 4, scenario: 4, titleKey: "scenario4.option1.content2Title", contentKey: "scenario4.option1.content2Citation" },
	{ label: "Scenario4 - Opción 2 (Triple terapia) - Título", order: 5, scenario: 4, titleKey: "scenario4.option2.label", contentKey: "scenario4.option2.content1" },
	{ label: "Scenario4 - Opción 2 - Esquema título y cita", order: 6, scenario: 4, titleKey: "scenario4.option2.content2Title", contentKey: "scenario4.option2.content2Citation" },
	{ label: "Scenario4 - Modal al seleccionar tratamiento", order: 7, scenario: 4, titleKey: "scenario4.modalLine1", contentKey: "scenario4.modalLine2" },
	{ label: "Intro escenario 5 (doble terapia)", order: 0, scenario: 5, titleKey: "scenario5.intro.title", contentKey: "scenario5.intro.content" },
	{ label: "Conversación (escenario 5 - doble terapia)", order: 1, scenario: 5, titleKey: null, contentKey: "scenario5.conversation.messages", type: "conversation" },
	{ label: "Scenario5 - Modal ¿Conoces estos objetivos?", order: 2, scenario: 5, titleKey: null, contentKey: "scenario5.conversation.explanationModal" },
	{ label: "Scenario5 - Explanation (gráfico e instrucción)", order: 3, scenario: 5, titleKey: "scenario5.explanation.instruction", contentKey: "scenario5.explanation.buttonText" },
	{ label: "Scenario5 - Pregunta quiz (título y curiosidad)", order: 4, scenario: 5, titleKey: "scenario5.question", contentKey: "scenario5.questionFootnote" },
	{ label: "Scenario5 - Footer Pregunta quiz", order: 5, scenario: 5, titleKey: null, contentKey: "scenario5.footer" },
	{ label: "Scenario5 - End (fin doble terapia)", order: 6, scenario: 5, titleKey: "scenario5.end.title", contentKey: "scenario5.end.content" },
	{ label: "Scenario5.2 - Intro2 - Título y subtítulo", order: 7, scenario: 5, titleKey: "scenario5.2.intro2.title", contentKey: "scenario5.2.intro2.subtitle" },
	{ label: "Scenario5.2 - Intro2 - Captión figura y cita bajo imagen", order: 8, scenario: 5, titleKey: "scenario5.2.intro2.caption", contentKey: "scenario5.2.intro2.imageCitation" },
	{ label: "Scenario5.2 - Intro2 - Modal Aurora 1 (instrucciones)", order: 9, scenario: 5, titleKey: null, contentKey: "scenario5.2.intro2.modalContent" },
	{ label: "Scenario5.2 - Instrucción y cita", order: 10, scenario: 5, titleKey: "scenario5.2.instruction", contentKey: "scenario5.2.citation" },
	{ label: "Scenario5.2 - Pie (RRC, UPCR)", order: 11, scenario: 5, titleKey: null, contentKey: "scenario5.2.footer" },
	{ label: "Scenario5.2 - Modal información pregunta (enunciado afirmación infiltrada)", order: 12, scenario: 5, titleKey: null, contentKey: "scenario5.2.questionInfo.enunciado" },
	{ label: "Scenario5.2-2 - Instrucción y cita", order: 13, scenario: 5, titleKey: "scenario5.2.2.instruction", contentKey: "scenario5.2.2.citation" },
	{ label: "Scenario5.2-2 - Modal acierto (justificación)", order: 14, scenario: 5, titleKey: "scenario5.2.2.modalCorrect.title", contentKey: "scenario5.2.2.modalCorrect.content" },
	{ label: "Scenario5.2 - End (enunciado y tratamiento)", order: 15, scenario: 5, titleKey: "scenario5.2.end.enunciado", contentKey: "scenario5.2.end.tratamiento" },
	{ label: "Conversación (escenario 6-intro)", order: 1, scenario: 6, titleKey: null, contentKey: "scenario6-intro.conversation.messages", type: "conversation" },
	{ label: "Scenario 6-intro - Enunciado", order: 2, scenario: 6, titleKey: "scenario6-intro.enunciado-title", contentKey: "scenario6-intro.enunciado" },
	{ label: "Scenario 6-intro - Enunciado test", order: 3, scenario: 6, titleKey: null, contentKey: "scenario6-intro.test-title" },
	{ label: "Scenario 6-intro Informe", order: 4, scenario: 6, titleKey: "scenario6-intro.informe-title", contentKey: "scenario6-intro.informe" },
	{ label: "Scenario 6 - Título", order: 5, scenario: 6, titleKey: "scenario6.title", contentKey: "scenario6.content" },
	{ label: "Scenario 6 - Recomendación de dosificación", order: 6, scenario: 6, titleKey: null, contentKey: "scenario6.recommendation-title" },
	{ label: "Scenario 6 - Modal todas correctas", order: 7, scenario: 6, titleKey: null, contentKey: "scenario6.modal-final" },
	{ label: "Scenario 6-end - Intro (TFGe y por lo tanto hay que)", order: 8, scenario: 6, titleKey: null, contentKey: "scenario6-end.intro" },
	{ label: "Scenario 6-end - Pasos numerados", order: 9, scenario: 6, titleKey: null, contentKey: "scenario6-end.steps" },
	{ label: "Scenario 7-intro - Título y subtítulo", order: 0, scenario: 7, titleKey: "scenario7-intro.title", contentKey: "scenario7-intro.subtitle" },
	{ label: "Scenario 7-intro - Cuerpo (preguntas)", order: 1, scenario: 7, titleKey: null, contentKey: "scenario7-intro.body" },
	{ label: "Scenario 7-intro - Modal (instrucción y cita)", order: 2, scenario: 7, titleKey: "scenario7-intro.instruction", contentKey: "scenario7-intro.citation" },
	{ label: "Scenario 7 - Instrucción (título)", order: 3, scenario: 7, titleKey: null, contentKey: "scenario7.instruction" },
	{ label: "Scenario 7 - Explicación (recomendación GLOSEN + cita)", order: 4, scenario: 7, titleKey: null, contentKey: "scenario7.explanation" },
	{ label: "Scenario 7.2 - Título", order: 6, scenario: 7, titleKey: null, contentKey: "scenario7.2.title" },
	{ label: "Scenario 7.3 - Título", order: 7, scenario: 7, titleKey: null, contentKey: "scenario7.3.title" },
	{ label: "Scenario 7-end - Contenido", order: 5, scenario: 7, titleKey: null, contentKey: "scenario7-end.content" },
	{ label: "Supertest-intro - Título y contenido", order: 0, scenario: 8, titleKey: "supertest-intro.title", contentKey: "supertest-intro.content" },
	{ label: "Supertest - Título y leyenda (NL/LES)", order: 1, scenario: 8, titleKey: "supertest.title", contentKey: "supertest.content" },
	{ label: "Supertest - Modal resultado: referencias (citas)", order: 2, scenario: 8, titleKey: null, contentKey: "supertest.result.references" },
	{ label: "Justificación - referencias (content)", order: 3, scenario: 8, titleKey: null, contentKey: "supertest.justification.content" },
	{ label: "Supertest-end - Título y contenido (¿Qué fue de Martina?)", order: 4, scenario: 8, titleKey: "supertest-end.title", contentKey: "supertest-end.content" },
	{ label: "End - Pie (ref. ES-LUP y fecha elaboración)", order: 0, scenario: 9, titleKey: "end.footerRef", contentKey: "end.footerDate" },

];

const PREVIEW_MAX_LENGTH = 120;

function stripHtml(html) {
	if (!html || typeof html !== "string") return "";
	const div = document.createElement("div");
	div.innerHTML = html;
	return (div.textContent || div.innerText || "").trim().replace(/\s+/g, " ");
}

function TextsPage() {
	const [games, setGames] = useState([]);
	const [selectedGameId, setSelectedGameId] = useState("");
	const [loading, setLoading] = useState(false);
	const [texts, setTexts] = useState([]);

	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editingRow, setEditingRow] = useState(null);
	const [editingTitle, setEditingTitle] = useState("");
	const [editingContent, setEditingContent] = useState("");
	const [filterScenario, setFilterScenario] = useState("");

	const [conversationDialogOpen, setConversationDialogOpen] = useState(false);
	const [editingConversationRow, setEditingConversationRow] = useState(null);
	const [editingConversationMessages, setEditingConversationMessages] = useState([]);

	useEffect(() => {
		getGames()
			.then((res) => {
				if (res.status === 200) {
					const loadedGames = res.data;
					setGames(loadedGames);

					// Preselect default game or first one
					if (loadedGames.length) {
						const defaultGame =
							loadedGames.find((g) => g.default) ||
							loadedGames[0];
						setSelectedGameId(defaultGame._id);
					}
				}
			})
			.catch((error) => {
				alertError({
					error,
					customMessage: "Could not get games.",
				});
			});
	}, []);

	useEffect(() => {
		if (!selectedGameId) return;

		setLoading(true);

		getTexts({ game: selectedGameId })
			.then((res) => {
				if (res.status === 200 && Array.isArray(res.data)) {
					setTexts(res.data);
				} else {
					setTexts([]);
				}
			})
			.catch((error) => {
				alertError({
					error,
					customMessage: "Could not get texts.",
				});
				setTexts([]);
			})
			.finally(() => setLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedGameId]);

	const tableData = useMemo(() => {
		return SECTIONS.map((section) => {
			const titleRecord = section.titleKey ? texts.find((t) => t.key === section.titleKey) : null;
			const contentRecord = section.contentKey ? texts.find((t) => t.key === section.contentKey) : null;
			const titleMeta = section.titleKey ? KEY_META[section.titleKey] : null;
			const contentMeta = section.contentKey ? KEY_META[section.contentKey] : null;
			const titleValue = titleRecord?.value ?? titleMeta?.defaultValue ?? "";
			const contentValue = contentRecord?.value ?? contentMeta?.defaultValue ?? "";
			return {
				id: section.label + section.order,
				sectionLabel: section.label,
				order: section.order,
				scenario: section.scenario,
				titleValue,
				contentValue,
				raw: {
					section,
					titleRecord: titleRecord || (section.titleKey ? { _id: null, key: section.titleKey, ...KEY_META[section.titleKey] } : null),
					contentRecord: contentRecord || (section.contentKey ? { _id: null, key: section.contentKey, ...KEY_META[section.contentKey] } : null),
				},
			};
		});
	}, [texts]);

	const scenarioOptions = useMemo(() => {
		const scenarios = [...new Set(SECTIONS.map((s) => s.scenario))].sort((a, b) => a - b);
		return scenarios;
	}, []);

	const filteredTableData = useMemo(() => {
		if (!filterScenario) return tableData;
		const num = parseInt(filterScenario, 10);
		return tableData.filter((row) => row.scenario === num);
	}, [tableData, filterScenario]);

	function parseConversationMessages(value) {
		if (!value || typeof value !== "string") return [];
		try {
			const arr = JSON.parse(value);
			return Array.isArray(arr) ? arr.map((m) => ({ speaker: m.speaker === "patient" ? "patient" : m.speaker === "prompt" ? "prompt" : m.speaker === "doctora" ? "doctora" : "doctor", content: m.content != null ? String(m.content) : "" })) : [];
		} catch {
			return [];
		}
	}

	const openEdit = (row) => {
		if (row.raw.section.type === "conversation") {
			setEditingConversationRow(row);
			setEditingConversationMessages(parseConversationMessages(row.contentValue));
			setConversationDialogOpen(true);
			return;
		}
		setEditingRow(row);
		setEditingTitle(row.titleValue || "");
		setEditingContent(row.contentValue || "");
		setEditDialogOpen(true);
	};

	const closeEdit = () => {
		setEditDialogOpen(false);
		setEditingRow(null);
		setEditingTitle("");
		setEditingContent("");
	};

	const closeConversationEdit = () => {
		setConversationDialogOpen(false);
		setEditingConversationRow(null);
		setEditingConversationMessages([]);
	};

	const updateOrCreateText = async (record, key, meta, value) => {
		if (record._id) {
			try {
				await updateText(record._id, { value });
			} catch (err) {
				if (err?.response?.status === 404) {
					await createText({
						game: selectedGameId,
						key,
						order: meta.order,
						scenario: meta.scenario,
						value,
					});
				} else throw err;
			}
		} else {
			await createText({
				game: selectedGameId,
				key,
				order: meta.order,
				scenario: meta.scenario,
				value,
			});
		}
	};

	const handleSaveEdit = async () => {
		if (!editingRow || !selectedGameId) return;
		const { section, titleRecord, contentRecord } = editingRow.raw;
		try {
			if (section.titleKey != null) {
				const meta = KEY_META[section.titleKey];
				await updateOrCreateText(titleRecord, section.titleKey, meta, editingTitle);
			}
			if (section.contentKey != null) {
				const meta = KEY_META[section.contentKey];
				await updateOrCreateText(contentRecord, section.contentKey, meta, editingContent);
			}
			const res = await getTexts({ game: selectedGameId });
			if (res.status === 200 && Array.isArray(res.data)) setTexts(res.data);
			alertSuccess({ customMessage: "Text saved successfully." });
			closeEdit();
		} catch (error) {
			alertError({ error, customMessage: "Could not save text." });
		}
	};

	const handleSaveConversation = async () => {
		if (!editingConversationRow || !selectedGameId) return;
		const { section, contentRecord } = editingConversationRow.raw;
		const meta = KEY_META[section.contentKey];
		const value = JSON.stringify(editingConversationMessages);
		try {
			await updateOrCreateText(contentRecord, section.contentKey, meta, value);
			const res = await getTexts({ game: selectedGameId });
			if (res.status === 200 && Array.isArray(res.data)) setTexts(res.data);
			alertSuccess({ customMessage: "Conversación guardada." });
			closeConversationEdit();
		} catch (error) {
			alertError({ error, customMessage: "No se pudo guardar la conversación." });
		}
	};

	function editFormatter(cell, row) {
		return (
			<Tooltip title="Edit">
				<Button size="small" onClick={() => openEdit(row)}>
					<EditIcon />
				</Button>
			</Tooltip>
		);
	}

	const previewFormatter = (cell, row) => {
		if (row?.raw?.section?.type === "conversation") {
			const arr = parseConversationMessages(cell);
			return <span>{arr.length} mensajes</span>;
		}
		const val = cell || "";
		const plain = val.includes("<") ? stripHtml(val) : val;
		const truncated = plain.length > PREVIEW_MAX_LENGTH ? plain.slice(0, PREVIEW_MAX_LENGTH) + "…" : plain;
		return <span className="text-break">{truncated}</span>;
	};

	const columns = [
		{ dataField: "sectionLabel", text: "Sección", sort: true },
		{ dataField: "titleValue", text: "Title", sort: false, formatter: previewFormatter },
		{ dataField: "contentValue", text: "Content", sort: false, formatter: previewFormatter },
		{
			dataField: "raw",
			text: "",
			formatter: editFormatter,
			headerAlign: "center",
			align: "center",
		},
	];

	return (
		<>
			<Card>
				<CardHeader title="Texts"></CardHeader>
				<CardBody>
					<div className="form-group">
						<label
							htmlFor="texts-game-select"
							className="font-weight-bold"
						>
							Select a game
						</label>
						<select
							id="texts-game-select"
							className="form-control"
							value={selectedGameId}
							onChange={(e) => {
								setSelectedGameId(e.target.value);
							}}
						>
							{games.map((game) => (
								<option key={game._id} value={game._id}>
									{game.fullName}
								</option>
							))}
						</select>
					</div>

					<hr className="my-4" />

					<div className="d-flex flex-wrap align-items-center mb-3">
						<span className="font-weight-bold mr-3">Texts list</span>
					</div>

					<div className="d-flex flex-wrap align-items-center mb-3">

						<label htmlFor="texts-scenario-filter" className="mb-0 mr-2">
							Filtrar por escenario:
						</label>
						<select
							id="texts-scenario-filter"
							className="form-control form-control-sm"
							style={{ width: "auto", minWidth: 100 }}
							value={filterScenario}
							onChange={(e) => setFilterScenario(e.target.value)}
						>
							<option value="">Todos</option>
							{scenarioOptions.map((s) => (
								<option key={s} value={s}>
									{s === 0 ? "Context" : s === 9 ? "Final" : s}
								</option>
							))}
						</select>
					</div>

					<Table data={filteredTableData} columns={columns} sizePerPage={100} />

					<Dialog
						open={editDialogOpen}
						onClose={closeEdit}
						maxWidth="lg"
						fullWidth
					>
						<DialogTitle>Edit text</DialogTitle>
						<DialogContent>
							{editingRow && (
								<>
									<div className="mb-3 text-muted">
										<strong>Sección:</strong> {editingRow.sectionLabel}
										{editingRow.raw.section.scenario !== undefined && (
											<>
												{" "}
												|{" "}
												<strong>Escenario:</strong>{" "}
												{editingRow.raw.section.scenario === 0 ? "Context" : editingRow.raw.section.scenario === 9 ? "Final" : editingRow.raw.section.scenario}
											</>
										)}
									</div>
									{editingRow.raw.section.titleKey != null && (
										<div className="mb-3" style={{ minHeight: 50 }}>
											<label className="d-block mb-2 font-weight-bold">Title</label>
											<Editor
												key={`${editingRow.sectionLabel}-title`}
												body={editingTitle}
												setBody={setEditingTitle}
												placeholder=""
												lang="es"
											/>
										</div>
									)}
									{editingRow.raw.section.contentKey != null && (
										<div style={{ minHeight: 280 }}>
											<label className="d-block mb-2 font-weight-bold">Content</label>
											<Editor
												key={`${editingRow.sectionLabel}-content`}
												body={editingContent}
												setBody={setEditingContent}
												placeholder=""
												lang="es"
											/>
										</div>
									)}
								</>
							)}
						</DialogContent>
						<DialogActions>
							<Button onClick={closeEdit}>Cancel</Button>
							<Button onClick={handleSaveEdit} color="primary" variant="contained">
								Save
							</Button>
						</DialogActions>
					</Dialog>

					<Dialog
						open={conversationDialogOpen}
						onClose={closeConversationEdit}
						maxWidth="md"
						fullWidth
					>
						<DialogTitle>Conversación (escenario 5) – Doctor / Paciente</DialogTitle>
						<DialogContent>
							<p className="text-muted mb-3">
								Orden: doctor y paciente alternados. Añade o elimina mensajes y reordena.
							</p>
							{editingConversationMessages.map((msg, index) => (
								<div
									key={index}
									className="d-flex align-items-start mb-3 p-3 border rounded"
									style={{ backgroundColor: msg.speaker === "doctor" ? "#f0f8ff" : msg.speaker === "doctora" ? "#e6e6fa" : msg.speaker === "prompt" ? "#e8f5e9" : "#fffef0" }}
								>
									<div className="d-flex flex-column mr-2">
										<select
											className="form-control form-control-sm mb-2"
											value={msg.speaker}
											onChange={(e) => {
												const next = [...editingConversationMessages];
												next[index] = { ...next[index], speaker: e.target.value };
												setEditingConversationMessages(next);
											}}
											style={{ width: 100 }}
										>
											<option value="doctor">Doctor</option>
											<option value="patient">Paciente</option>
											<option value="doctora">Doctora</option>
											<option value="prompt">Pregunta / texto destacado</option>
										</select>
										<Tooltip title="Subir">
											<IconButton
												size="small"
												disabled={index === 0}
												onClick={() => {
													const next = [...editingConversationMessages];
													[next[index - 1], next[index]] = [next[index], next[index - 1]];
													setEditingConversationMessages(next);
												}}
											>
												<ArrowUpwardIcon />
											</IconButton>
										</Tooltip>
										<Tooltip title="Bajar">
											<IconButton
												size="small"
												disabled={index === editingConversationMessages.length - 1}
												onClick={() => {
													const next = [...editingConversationMessages];
													[next[index], next[index + 1]] = [next[index + 1], next[index]];
													setEditingConversationMessages(next);
												}}
											>
												<ArrowDownwardIcon />
											</IconButton>
										</Tooltip>
										<Tooltip title="Eliminar mensaje">
											<IconButton
												size="small"
												onClick={() => {
													setEditingConversationMessages(editingConversationMessages.filter((_, i) => i !== index));
												}}
											>
												<DeleteIcon />
											</IconButton>
										</Tooltip>
									</div>
									<div className="flex-grow-1" style={{ minWidth: 0, minHeight: 80 }}>
										<Editor
											key={`conv-${index}-${editingConversationMessages.length}`}
											body={msg.content}
											setBody={(val) => {
												const next = [...editingConversationMessages];
												next[index] = { ...next[index], content: val };
												setEditingConversationMessages(next);
											}}
											placeholder="Texto del mensaje..."
											lang="es"
										/>
									</div>
								</div>
							))}
							<Button
								startIcon={<AddIcon />}
								onClick={() => setEditingConversationMessages([...editingConversationMessages, { speaker: "doctor", content: "" }])}
								className="mb-2"
							>
								Añadir mensaje
							</Button>
						</DialogContent>
						<DialogActions>
							<Button onClick={closeConversationEdit}>Cancelar</Button>
							<Button onClick={handleSaveConversation} color="primary" variant="contained">
								Guardar conversación
							</Button>
						</DialogActions>
					</Dialog>
				</CardBody>
			</Card>
		</>
	);
}

export default TextsPage;

